/**
 * KAN-925: несколько вошедших аккаунтов платформы в одном браузере (как в Google).
 *
 * Хранилище:
 *   localStorage   smeta.accounts.v1          список аккаунтов (JSON-массив)
 *   localStorage   smeta.accounts.lastActive  userId последнего выбранного (для новой вкладки)
 *   sessionStorage smeta.accounts.active      userId активного в этой вкладке
 * Активный аккаунт вкладки: указатель вкладки. Вкладка без указателя (новая,
 * после миграции) при первом чтении берёт lastActive (иначе первый) и сразу
 * закрепляет его в sessionStorage: дальше другие вкладки её не двигают.
 * Указатель на удалённый аккаунт (вышел в другой вкладке) = вкладка вышла:
 * токена нет, отката на другой аккаунт нет (иначе вкладка показывала бы A, а
 * слала токен B).
 *
 * Старый формат (authToken/refreshToken) при первом чтении становится первым
 * аккаунтом: userId из claim user_id в JWT. Токен без user_id (старые сессии,
 * фейковые токены тестов) хранится под временным ключом до первого /auth/me,
 * который дописывает настоящий userId (updateAccountProfile).
 *
 * Все обращения к хранилищу в try/catch: без хранилища вошедших нет.
 */
import { cabinetUserLabel } from './cabinetIdentity';
import { clearSessionManager } from './sessionManager';

export const ACCOUNTS_STORAGE_KEY = 'smeta.accounts.v1';
export const LAST_ACTIVE_ACCOUNT_KEY = 'smeta.accounts.lastActive';
export const TAB_ACTIVE_ACCOUNT_KEY = 'smeta.accounts.active';
/** Временный ключ старого токена без user_id (до первого /auth/me). */
export const LEGACY_ACCOUNT_ID = 'legacy-session';
/** Настоящий userId временного аккаунта после /auth/me: другие вкладки с указателем на временный ключ находят его. */
export const LEGACY_RENAMED_KEY = 'smeta.accounts.legacyRenamed';

const LEGACY_ACCESS_KEY = 'authToken';
const LEGACY_REFRESH_KEY = 'refreshToken';

export interface Account {
  userId: string;
  email: string;
  name: string;
  role: string | null;
  organizationId: string | null;
  organizationName: string | null;
  access: string;
  refresh: string | null;
}

/** Пользователь из ответа входа (organization: id) или /auth/me (organization: {id, name}). */
export interface AccountUserLike {
  id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: string | null;
  manager_name?: string | null;
  organization?: string | { id: string; name?: string } | null;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

function storage(kind: 'localStorage' | 'sessionStorage'): Storage | null {
  try {
    return window[kind];
  } catch {
    return null;
  }
}

function getItem(kind: 'localStorage' | 'sessionStorage', key: string): string | null {
  try {
    return storage(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function setItem(kind: 'localStorage' | 'sessionStorage', key: string, value: string | null): void {
  try {
    const target = storage(kind);
    if (!target) return;
    if (value === null) target.removeItem(key);
    else target.setItem(key, value);
  } catch {
    // хранилище недоступно
  }
}

function isAccount(value: unknown): value is Account {
  if (!value || typeof value !== 'object') return false;
  const { userId, access } = value as Record<string, unknown>;
  return typeof userId === 'string' && userId !== '' && typeof access === 'string';
}

function parseAccounts(raw: string | null): Account[] {
  if (!raw) return [];
  try {
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value) ? value.filter(isAccount) : [];
  } catch {
    return [];
  }
}

function writeAccounts(accounts: Account[]): void {
  setItem('localStorage', ACCOUNTS_STORAGE_KEY, accounts.length ? JSON.stringify(accounts) : null);
}

/** claim user_id из payload JWT; нет или токен не JWT: null. */
export function userIdFromToken(token: string): string | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const value: unknown = JSON.parse(atob(padded));
    if (!value || typeof value !== 'object') return null;
    const id = (value as Record<string, unknown>).user_id;
    return typeof id === 'string' || typeof id === 'number' ? String(id) : null;
  } catch {
    return null;
  }
}

/** claim exp из payload JWT (секунды); нет или токен не JWT: 0. */
function tokenExp(token: string | null): number {
  const payload = token?.split('.')[1];
  if (!payload) return 0;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const value: unknown = JSON.parse(atob(padded));
    const exp = value && typeof value === 'object' ? (value as Record<string, unknown>).exp : null;
    return typeof exp === 'number' ? exp : 0;
  } catch {
    return 0;
  }
}

/** Старые authToken/refreshToken становятся аккаунтом (один раз, без уведомления). */
function migrateLegacy(): void {
  const access = getItem('localStorage', LEGACY_ACCESS_KEY);
  if (access === null) return;
  const refresh = getItem('localStorage', LEGACY_REFRESH_KEY);
  setItem('localStorage', LEGACY_ACCESS_KEY, null);
  setItem('localStorage', LEGACY_REFRESH_KEY, null);
  if (access.trim() === '') return;
  const userId = userIdFromToken(access) ?? LEGACY_ACCOUNT_ID;
  const accounts = parseAccounts(getItem('localStorage', ACCOUNTS_STORAGE_KEY));
  const existing = accounts.find((a) => a.userId === userId);
  if (existing) {
    // старые ключи пишет только старый код (вкладка до обновления): его пара новее
    existing.access = access;
    if (refresh) existing.refresh = refresh;
    writeAccounts(accounts);
  } else {
    if (userId === LEGACY_ACCOUNT_ID) setItem('localStorage', LEGACY_RENAMED_KEY, null);
    accounts.push({
      userId,
      email: '',
      name: '',
      role: null,
      organizationId: null,
      organizationName: null,
      access,
      refresh: refresh || null,
    });
    writeAccounts(accounts);
  }
  if (!getItem('localStorage', LAST_ACTIVE_ACCOUNT_KEY)) {
    setItem('localStorage', LAST_ACTIVE_ACCOUNT_KEY, userId);
  }
}

export function readAccounts(): Account[] {
  migrateLegacy();
  return parseAccounts(getItem('localStorage', ACCOUNTS_STORAGE_KEY));
}

/** Временный ключ, уже получивший настоящий userId, ведёт на него. */
function resolveId(userId: string): string {
  if (userId !== LEGACY_ACCOUNT_ID) return userId;
  return getItem('localStorage', LEGACY_RENAMED_KEY) ?? userId;
}

export function findAccount(userId: string): Account | null {
  const id = resolveId(userId);
  return readAccounts().find((a) => a.userId === id) ?? null;
}

/**
 * Аккаунт вкладки. Есть указатель: только он (удалённый = вкладка вышла, null).
 * Нет указателя: lastActive или первый, и указатель сразу закрепляется.
 */
function pickActive(accounts: Account[]): Account | null {
  const tab = getItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY);
  if (tab) {
    const id = resolveId(tab);
    const found = accounts.find((a) => a.userId === id) ?? null;
    if (found && id !== tab) setItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY, id);
    return found;
  }
  const last = getItem('localStorage', LAST_ACTIVE_ACCOUNT_KEY);
  const chosen = accounts.find((a) => a.userId === last) ?? accounts[0] ?? null;
  if (chosen) setItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY, chosen.userId);
  return chosen;
}

export function getActiveAccount(): Account | null {
  return pickActive(readAccounts());
}

export function getActiveAccessToken(): string | null {
  return getActiveAccount()?.access ?? null;
}

function pointTo(userId: string | null): void {
  setItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY, userId);
  setItem('localStorage', LAST_ACTIVE_ACCOUNT_KEY, userId);
}

function organizationOf(user: AccountUserLike): { id: string | null; name: string | null } {
  const org = user.organization;
  if (!org) return { id: null, name: null };
  if (typeof org === 'string') return { id: org, name: null };
  return { id: org.id ?? null, name: org.name ?? null };
}

function profileFields(user: AccountUserLike, previous?: Account): Partial<Account> {
  const org = organizationOf(user);
  const role = user.role ?? previous?.role ?? null;
  const email = user.email ?? previous?.email ?? '';
  const name =
    cabinetUserLabel({ ...user, email, role: role ?? undefined }).name || previous?.name || email;
  return {
    email,
    name,
    role,
    organizationId: org.id ?? previous?.organizationId ?? null,
    // ответ входа даёт только id: название прошлого профиля годится, пока кабинет тот же
    organizationName:
      org.name ?? (org.id === null || org.id === previous?.organizationId ? previous?.organizationName ?? null : null),
  };
}

/** Вход: аккаунт добавляется (тот же userId обновляется) и становится активным во вкладке. */
export function saveAccountFromLogin(
  access: string,
  refresh: string | null | undefined,
  user: AccountUserLike
): Account {
  const accounts = readAccounts();
  const index = accounts.findIndex((a) => a.userId === user.id);
  const previous = index >= 0 ? accounts[index] : undefined;
  const account: Account = {
    userId: user.id,
    email: '',
    name: '',
    role: null,
    organizationId: null,
    organizationName: null,
    ...previous,
    ...profileFields(user, previous),
    access,
    refresh: refresh ?? previous?.refresh ?? null,
  };
  if (index >= 0) accounts[index] = account;
  else accounts.push(account);
  writeAccounts(accounts);
  pointTo(account.userId);
  notify();
  return account;
}

/**
 * Профиль из /auth/me дописывает имя, роль и кабинет. Аккаунт под временным
 * ключом получает настоящий userId (если такой уже вошёл, временный сливается с ним).
 */
export function updateAccountProfile(userId: string, profile: AccountUserLike): void {
  const id = resolveId(userId);
  const accounts = readAccounts();
  const index = accounts.findIndex((a) => a.userId === id);
  if (index < 0) return;
  // профиль другого пользователя (ответ пришёл не на тот аккаунт) не пишется
  if (id !== LEGACY_ACCOUNT_ID && profile.id && profile.id !== id) return;
  const current = accounts[index];
  const realId = id === LEGACY_ACCOUNT_ID && profile.id ? profile.id : id;
  let updated: Account = { ...current, ...profileFields(profile, current), userId: realId };
  if (realId !== id) {
    setItem('localStorage', LEGACY_RENAMED_KEY, realId);
    const existing = accounts.findIndex((a) => a.userId === realId);
    if (existing >= 0) {
      // этот пользователь уже вошёл новым кодом: остаётся более свежая пара (по exp access)
      const other = accounts[existing];
      if (tokenExp(other.access) >= tokenExp(current.access)) {
        updated = { ...updated, access: other.access, refresh: other.refresh };
      }
      accounts.splice(existing, 1);
    }
    if (getItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY) === id) {
      setItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY, realId);
    }
    if (getItem('localStorage', LAST_ACTIVE_ACCOUNT_KEY) === id) {
      setItem('localStorage', LAST_ACTIVE_ACCOUNT_KEY, realId);
    }
  }
  const target = accounts.findIndex((a) => a.userId === id);
  accounts[target] = updated;
  writeAccounts(accounts);
  notify();
}

/** Новая пара после refresh; refresh без ротации остаётся прежним. */
export function updateAccountTokens(userId: string, access: string, refresh?: string | null): void {
  const id = resolveId(userId);
  const accounts = readAccounts();
  const account = accounts.find((a) => a.userId === id);
  if (!account) return;
  account.access = access;
  if (refresh) account.refresh = refresh;
  writeAccounts(accounts);
  notify();
}

/** Выход одного аккаунта; возвращает активный после удаления (или null). */
export function removeAccount(userId: string): Account | null {
  const id = resolveId(userId);
  const accounts = readAccounts();
  const rest = accounts.filter((a) => a.userId !== id);
  const wasActive = pickActive(accounts)?.userId === id;
  writeAccounts(rest);
  clearSessionManager(id);
  if (getItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY) === id) {
    setItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY, null);
  }
  if (getItem('localStorage', LAST_ACTIVE_ACCOUNT_KEY) === id) {
    setItem('localStorage', LAST_ACTIVE_ACCOUNT_KEY, null);
  }
  const next = pickActive(rest);
  if (wasActive || !next) pointTo(next?.userId ?? null);
  notify();
  return next;
}

/** Выход из всех аккаунтов; прочие настройки браузера остаются. */
export function removeAllAccounts(): void {
  readAccounts().forEach((a) => clearSessionManager(a.userId));
  writeAccounts([]);
  pointTo(null);
  notify();
}

/** Переключить активный аккаунт этой вкладки (неизвестный userId ничего не меняет). */
export function setActiveAccount(userId: string): void {
  const id = resolveId(userId);
  if (!readAccounts().some((a) => a.userId === id)) return;
  pointTo(id);
  notify();
}

/** Подписка: изменения в этой вкладке и список аккаунтов из соседних (событие storage). */
export function subscribeAccounts(listener: Listener): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === ACCOUNTS_STORAGE_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export interface AccountsSnapshot {
  accounts: Account[];
  active: Account | null;
}

let snapshotKey: string | null = null;
let snapshot: AccountsSnapshot = { accounts: [], active: null };

/** Стабильный снимок для useSyncExternalStore: новый объект только при изменении хранилища. */
export function getAccountsSnapshot(): AccountsSnapshot {
  // миграция и закрепление вкладки до ключа снимка: иначе второй вызов дал бы новый объект
  getActiveAccount();
  const key = [
    getItem('localStorage', ACCOUNTS_STORAGE_KEY),
    getItem('localStorage', LAST_ACTIVE_ACCOUNT_KEY),
    getItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY),
  ].join('\u0000');
  if (key !== snapshotKey) {
    const accounts = readAccounts();
    snapshotKey = key;
    snapshot = { accounts, active: pickActive(accounts) };
  }
  return snapshot;
}

/**
 * KAN-925: аккаунт этой вкладки удалён в другой вкладке (выход, протухший
 * refresh). Указатель вкладки снимается и вызывается onLost (полная
 * перезагрузка): после неё вкладка возьмёт lastActive, данные не смешиваются.
 * Вкладка без аккаунта (никого не осталось) по событиям не перезагружается.
 * Возвращает отписку.
 */
export function watchTabAccount(onLost: () => void): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== ACCOUNTS_STORAGE_KEY) return;
    if (!getItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY)) return;
    if (getActiveAccount() !== null) return;
    setItem('sessionStorage', TAB_ACTIVE_ACCOUNT_KEY, null);
    onLost();
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}
